import Vue from 'vue';
import {
	API_CREATE_MEDIA_UPLOAD,
	API_GET_MEDIA_UPLOAD,
	API_GET_MEDIA_SUGGESTIONS,
	API_PATCH_USER_MEDIA_SELF
} from '@/api-service.js';

export const UPLOAD_LIMITS = {
	MAX_FILE_BYTES: 30 * 1024 * 1024,
	GALLERY_CAP: 50,
	CONCURRENCY: 3,
	MAX_POLL_MS: 3 * 60 * 1000,
	FAST_POLL_MS: 1000,
	FAST_POLL_WINDOW_MS: 10 * 1000,
	SLOW_POLL_MS: 3000
};

export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];
export const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
export const PHOTO_GUIDELINES_URL = '/public_profile_photo_guidelines/';

export const APPROVED_TAGS = [
	'architecture', 'art', 'astro', 'birds', 'bw', 'celebrity', 'conservation', 'epic',
	'fashion', 'food', 'ice', 'landscape', 'macro', 'music', 'nature', 'pets', 'portrait',
	'sports', 'street', 'travel', 'underwater', 'water', 'wedding', 'wildlife'
];

const TERMINAL_STATUSES = ['done', 'rejected', 'error'];

// Real API on staging. Mock stays available for UI-only work:
// localStorage.setItem('aupUploadMock', '1') then reload. '0' or unset = live API.
const UPLOAD_MOCK_DEFAULT = false;

function readMockFlag() {
	try {
		const stored = window.localStorage.getItem('aupUploadMock');
		if (stored === '1') {
			return true;
		}
		if (stored === '0') {
			return false;
		}
	} catch (error) {
		// localStorage can be unavailable (private mode); fall back to the default
	}
	return UPLOAD_MOCK_DEFAULT;
}

export const USE_UPLOAD_MOCK = typeof window !== 'undefined' && readMockFlag();

if (USE_UPLOAD_MOCK) {
	console.warn('[upload-service] MOCK MODE: photo uploads, AI suggestions and photo detail saves are simulated, nothing reaches the API.');
}

export function isMockMedia(item) {
	return !!item && String(item.ppd_usermediaid || '').startsWith('mock-');
}

const getGuid = () => Vue.$cookies.get('au_guid');

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export function uploadError(reason, extra = {}) {
	const error = new Error(reason);
	error.reason = reason;
	Object.assign(error, extra);
	return error;
}

export function getFileExtension(file) {
	const name = (file && file.name ? file.name : '').toLowerCase();
	return name.includes('.') ? name.split('.').pop() : '';
}

// Browser checks are for speed only; the server makes the final decision.
export function checkFileLocally(file) {
	if (!file) {
		return 'missing-file';
	}
	const typeOk = ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(getFileExtension(file));
	if (!typeOk) {
		return 'filetype';
	}
	if (file.size > UPLOAD_LIMITS.MAX_FILE_BYTES) {
		return 'filesize';
	}
	return null;
}

function contentTypeFor(file) {
	if (file.type) {
		return file.type;
	}
	return getFileExtension(file) === 'png' ? 'image/png' : 'image/jpeg';
}

export function uploadErrorMessage(reason, extra = {}) {
	switch (reason) {
		case 'filetype':
			return {text: 'Only JPEG and PNG photos can be uploaded.'};
		case 'filesize':
			return {text: 'This photo is larger than 30 MB.'};
		case 'dimensions':
			return {text: 'This photo is too small or has unusual dimensions.'};
		case 'moderation':
			return {
				text: 'This photo doesn\'t meet our community guidelines.',
				link: PHOTO_GUIDELINES_URL,
				linkText: 'Photo guidelines'
			};
		case 'gallery-full': {
			const cap = extra.cap || UPLOAD_LIMITS.GALLERY_CAP;
			return {text: `Your gallery is full (${cap} photos and videos). Remove some to add more.`};
		}
		case 'rate-limited': {
			const minutes = Math.max(1, Math.ceil((Number(extra.retryAfter) || 60) / 60));
			return {text: `You've uploaded a lot of photos recently. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`};
		}
		case 'network':
			return {text: 'Couldn\'t reach the server. Check your connection and try again.'};
		case 'alt-required':
			return {text: 'Alt text is required.'};
		case 'upload-in-progress':
			return {text: 'Your last upload is still finishing — try again in a few minutes.'};
		case 'missing-file':
			return {text: 'Please choose your profile photo again'};
		default:
			return {text: 'Something went wrong on our side. Please try again.'};
	}
}

// Short reason for one-line rows ("Larger than 30 MB").
export function uploadErrorShort(reason) {
	switch (reason) {
		case 'filetype': return 'Not a JPEG or PNG';
		case 'filesize': return 'Larger than 30 MB';
		case 'dimensions': return 'Too small or unusual dimensions';
		case 'moderation': return 'Doesn\'t meet community guidelines';
		case 'gallery-full': return 'Gallery is full';
		case 'rate-limited': return 'Too many uploads recently';
		case 'network': return 'Couldn\'t reach the server';
		case 'upload-in-progress': return 'Upload still in progress';
		case 'missing-file': return 'Please choose your photo again';
		default: return 'Something went wrong';
	}
}

function isUploadInProgressResponse(data) {
	const message = String((data && data.message) || '').toLowerCase();
	const error = String((data && data.error) || '').toLowerCase();
	return message.includes('already in progress') || error.includes('already in progress');
}

function errorFromApiResponse(error) {
	const response = error && error.response;
	if (!response) {
		return uploadError('network');
	}
	const data = response.data || {};
	if (response.status === 409) {
		return uploadError('gallery-full', {galleryCount: data.galleryCount, cap: data.cap});
	}
	if (response.status === 429) {
		const header = response.headers && (response.headers['retry-after'] || response.headers['Retry-After']);
		return uploadError('rate-limited', {retryAfter: data.retryAfter || header});
	}
	if (response.status === 400 && isUploadInProgressResponse(data)) {
		return uploadError('upload-in-progress', {status: response.status});
	}
	if (response.status === 400 && ['filetype', 'filesize', 'alt-required'].includes(data.error)) {
		return uploadError(data.error);
	}
	return uploadError('unknown', {status: response.status});
}

export function postToS3(post, file, onProgress) {
	return new Promise((resolve, reject) => {
		const form = new FormData();
		Object.entries(post.fields || {}).forEach(([key, value]) => form.append(key, value));
		form.append('file', file);

		const xhr = new XMLHttpRequest();
		xhr.open('POST', post.url, true);
		xhr.withCredentials = false;
		xhr.upload.onprogress = event => {
			if (event.lengthComputable && onProgress) {
				onProgress(Math.round((event.loaded / event.total) * 100));
			}
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				if (onProgress) {
					onProgress(100);
				}
				resolve();
			} else if (xhr.status === 403) {
				reject(uploadError('filesize'));
			} else {
				reject(uploadError('unknown', {status: xhr.status}));
			}
		};
		xhr.onerror = () => reject(uploadError('network'));
		xhr.ontimeout = () => reject(uploadError('network'));
		xhr.send(form);
	});
}

export async function pollUpload(uploadId, onStatus) {
	const startedAt = Date.now();
	let lastStatus = null;
	for (;;) {
		const elapsed = Date.now() - startedAt;
		if (elapsed > UPLOAD_LIMITS.MAX_POLL_MS) {
			throw uploadError('error');
		}
		await sleep(elapsed < UPLOAD_LIMITS.FAST_POLL_WINDOW_MS ? UPLOAD_LIMITS.FAST_POLL_MS : UPLOAD_LIMITS.SLOW_POLL_MS);

		let data;
		try {
			const response = await API_GET_MEDIA_UPLOAD(uploadId, getGuid());
			data = response.data || {};
		} catch (error) {
			// A dropped poll is retried until the overall timeout; a 404 will not recover.
			if (error && error.response && error.response.status === 404) {
				throw uploadError('unknown');
			}
			continue;
		}

		if (data.status !== lastStatus) {
			lastStatus = data.status;
			if (onStatus) {
				onStatus(data.status);
			}
		}
		if (data.status === 'done') {
			return {
				media: data.media || null,
				suggestions: normalizeSuggestions(data.suggestions)
			};
		}
		if (TERMINAL_STATUSES.includes(data.status)) {
			throw uploadError(data.reason || 'unknown', {status: data.status});
		}
	}
}

export async function uploadPhoto(file, uploadAs, {onProgress, onStatus} = {}) {
	if (USE_UPLOAD_MOCK) {
		return mockUploadPhoto(file, uploadAs, {onProgress, onStatus});
	}

	const localReason = checkFileLocally(file);
	if (localReason) {
		throw uploadError(localReason);
	}

	let created;
	try {
		const response = await API_CREATE_MEDIA_UPLOAD({
			uploadAs,
			fileName: file.name,
			size: file.size,
			contentType: contentTypeFor(file)
		}, getGuid());
		created = response.data || {};
	} catch (error) {
		throw errorFromApiResponse(error);
	}

	if (!created.uploadId || !created.post || !created.post.url) {
		throw uploadError('unknown');
	}

	if (onStatus) {
		onStatus('uploading');
	}
	await postToS3(created.post, file, onProgress);

	if (onStatus) {
		onStatus('processing');
	}
	return pollUpload(created.uploadId, status => {
		if (onStatus && status === 'processing') {
			onStatus('processing');
		}
	});
}

// Runs uploads with at most UPLOAD_LIMITS.CONCURRENCY in flight. Each item must
// have a `file`. Handlers are called per item as soon as that item finishes.
export function uploadPhotoQueue(items, uploadAs, {onProgress, onStatus, onDone, onError} = {}) {
	const queue = items.slice();
	let stopReason = null;

	const runNext = async () => {
		while (queue.length) {
			const item = queue.shift();
			if (stopReason) {
				if (onError) {
					onError(item, stopReason);
				}
				continue;
			}
			try {
				const result = await uploadPhoto(item.file, uploadAs, {
					onProgress: percent => onProgress && onProgress(item, percent),
					onStatus: status => onStatus && onStatus(item, status)
				});
				if (onDone) {
					onDone(item, result);
				}
			} catch (error) {
				const normalized = error && error.reason ? error : uploadError('unknown');
				// Every later request would get the same answer from the server. The mock decides
				// per file name, so there each file keeps its own scenario.
				if (!USE_UPLOAD_MOCK && ['gallery-full', 'rate-limited'].includes(normalized.reason)) {
					stopReason = normalized;
				}
				if (onError) {
					onError(item, normalized);
				}
			}
		}
	};

	const workers = [];
	for (let i = 0; i < Math.min(UPLOAD_LIMITS.CONCURRENCY, items.length); i++) {
		workers.push(runNext());
	}
	return Promise.all(workers);
}

let uploadItemSeq = 0;

// One reactive row per chosen file, consumed by the review step (EditImageDetails, mode 'upload').
export function createUploadItem(file) {
	const reason = checkFileLocally(file);
	uploadItemSeq += 1;
	return {
		localId: `upload-${Date.now()}-${uploadItemSeq}`,
		file,
		fileName: file.name,
		previewUrl: reason === 'filetype' ? '' : URL.createObjectURL(file),
		status: reason ? 'rejected' : 'queued',
		progress: 0,
		media: null,
		suggestions: null,
		suggestionsFailed: false,
		reason,
		reasonExtra: {},
		submitted: false,
		form: null
	};
}

export function revokeUploadItems(items) {
	items.forEach(item => {
		if (item.previewUrl) {
			URL.revokeObjectURL(item.previewUrl);
		}
	});
}

// Uploads every queued item and keeps its status/progress/media in sync.
export function startUploadItems(items, uploadAs, {onReady} = {}) {
	const queued = items.filter(item => item.status === 'queued');
	return uploadPhotoQueue(queued, uploadAs, {
		onProgress: (item, percent) => {
			item.progress = percent;
		},
		onStatus: (item, status) => {
			if (status === 'uploading' || status === 'processing') {
				item.status = status;
			}
		},
		onDone: (item, result) => {
			if (!result || !result.media) {
				item.status = 'rejected';
				item.reason = 'unknown';
				return;
			}
			item.media = result.media;
			item.suggestions = result.suggestions;
			item.form = createReviewForm(result.media, result.suggestions);
			item.status = 'ready';
			if (onReady) {
				onReady(item);
			}
		},
		onError: (item, error) => {
			item.status = 'rejected';
			item.reason = error.reason || 'unknown';
			item.reasonExtra = {
				cap: error.cap,
				galleryCount: error.galleryCount,
				retryAfter: error.retryAfter
			};
		}
	});
}

// Staging sometimes returns the contracted flat shape, sometimes the Lambda wrap
// { ok: true, ai: { alt, title, description, tags } }. Always normalize to flat.
export function normalizeSuggestions(payload) {
	if (!payload || typeof payload !== 'object') {
		return null;
	}
	const source = payload.ai && typeof payload.ai === 'object' ? payload.ai : payload;
	const tags = Array.isArray(source.tags)
		? source.tags.map(tag => (tag && typeof tag === 'object' ? tag.tag : tag)).filter(Boolean)
		: [];
	const suggestions = {
		alt: source.alt ? String(source.alt) : '',
		title: source.title ? String(source.title) : '',
		description: source.description ? String(source.description) : '',
		tags: parseTags(tags)
	};
	if (!suggestions.alt && !suggestions.title && !suggestions.description && !suggestions.tags.length) {
		return null;
	}
	return suggestions;
}

export async function fetchSuggestions(usermediaid) {
	if (!usermediaid) {
		return null;
	}
	if (USE_UPLOAD_MOCK) {
		await sleep(600);
		return mockSuggestions();
	}
	try {
		const response = await API_GET_MEDIA_SUGGESTIONS(usermediaid, getGuid());
		return normalizeSuggestions(response.data);
	} catch (error) {
		return null;
	}
}

export async function saveMediaDetails(payload) {
	if (USE_UPLOAD_MOCK) {
		await sleep(400);
		const items = Array.isArray(payload) ? payload : [payload];
		if (items.some(item => 'ppd_altdescription' in item && !String(item.ppd_altdescription || '').trim())) {
			throw uploadError('alt-required');
		}
		return {data: payload};
	}
	try {
		return await API_PATCH_USER_MEDIA_SELF(payload);
	} catch (error) {
		throw errorFromApiResponse(error);
	}
}

/////////////////////////////////////////////////////////////////////////////
//// REVIEW FORM HELPERS
/////////////////////////////////////////////////////////////////////////////
export const REVIEW_LIMITS = {
	TITLE: 80,
	DESCRIPTION: 80,
	ALT: 200
};

export function parseTags(value) {
	const list = Array.isArray(value) ? value : String(value || '').split(',');
	const tags = list.map(tag => String(tag).trim().toLowerCase()).filter(tag => APPROVED_TAGS.includes(tag));
	return [...new Set(tags)];
}

// Alt text and tags come from suggestions only when the photo has none of its own.
export function createReviewForm(media, suggestions) {
	const item = media || {};
	const form = {
		title: item.ppd_displaytitle || '',
		description: item.ppd_displaydescription || '',
		alt: item.ppd_altdescription || '',
		tags: parseTags(item.ppd_categorytags),
		focallength: item.ppd_focallength || '',
		fnumber: item.ppd_fnumber || '',
		exposuretime: item.ppd_exposuretime || '',
		iso: item.ppd_iso == null ? '' : String(item.ppd_iso)
	};
	applySuggestionsToForm(form, suggestions);
	return form;
}

export function applySuggestionsToForm(form, suggestions) {
	if (!form || !suggestions) {
		return form;
	}
	if (!form.alt && suggestions.alt) {
		form.alt = String(suggestions.alt).slice(0, REVIEW_LIMITS.ALT);
	}
	if (!form.tags.length && suggestions.tags && suggestions.tags.length) {
		form.tags = parseTags(suggestions.tags);
	}
	return form;
}

export function buildMediaPayload(media, form, {includeAlt = true} = {}) {
	const payload = {
		...media,
		ppd_displaytitle: form.title.trim(),
		ppd_displaydescription: form.description.trim(),
		ppd_categorytags: form.tags.join(','),
		ppd_focallength: form.focallength,
		ppd_fnumber: form.fnumber,
		ppd_exposuretime: form.exposuretime,
		ppd_iso: form.iso
	};
	// `alt` is derived by the API and never sent back.
	delete payload.alt;
	if (includeAlt) {
		payload.ppd_altdescription = form.alt.trim();
	} else {
		delete payload.ppd_altdescription;
	}
	return payload;
}

/////////////////////////////////////////////////////////////////////////////
//// DEV STUB
/////////////////////////////////////////////////////////////////////////////
function mockSuggestions() {
	return {
		alt: 'A great blue heron wades through shallow water lined with red flowering plants in warm light.',
		title: 'Heron in the Marsh',
		description: 'Caught this patient hunter stalking through a colorful wetland.',
		tags: ['wildlife', 'birds']
	};
}

async function mockUploadPhoto(file, uploadAs, {onProgress, onStatus}) {
	const localReason = checkFileLocally(file);
	if (localReason) {
		throw uploadError(localReason);
	}
	await sleep(300);
	const name = file.name.toLowerCase();
	// errors returned by POST /media/uploads, before the S3 step
	if (name.includes('full')) {
		throw uploadError('gallery-full', {galleryCount: UPLOAD_LIMITS.GALLERY_CAP, cap: UPLOAD_LIMITS.GALLERY_CAP});
	}
	if (name.includes('ratelimit')) {
		throw uploadError('rate-limited', {retryAfter: 300});
	}
	if (name.includes('offline')) {
		throw uploadError('network');
	}

	if (onStatus) {
		onStatus('uploading');
	}
	for (let percent = 0; percent <= 100; percent += 20) {
		if (onProgress) {
			onProgress(percent);
		}
		await sleep(200);
	}
	if (onStatus) {
		onStatus('processing');
	}
	await sleep(name.includes('slow') ? 8000 : 1500 + Math.random() * 2500);

	if (name.includes('nsfw')) {
		throw uploadError('moderation', {status: 'rejected'});
	}
	if (name.includes('small')) {
		throw uploadError('dimensions', {status: 'rejected'});
	}
	if (name.includes('fail')) {
		throw uploadError('upstream', {status: 'error'});
	}
	const suggestions = name.includes('noai') ? null : mockSuggestions();
	const tags = name.includes('notags') || !suggestions ? [] : suggestions.tags;
	// the real API reads EXIF from the file; PNGs normally have none
	const exif = contentTypeFor(file) === 'image/jpeg'
		? {
			ppd_cameramake: 'Sony',
			ppd_cameramodel: 'ILCE-1',
			ppd_lensmodel: 'FE 600mm F4 GM OSS',
			ppd_focallength: '600mm',
			ppd_fnumber: 'f/4',
			ppd_exposuretime: '1/2000',
			ppd_iso: '800'
		}
		: {
			ppd_cameramake: '',
			ppd_cameramodel: '',
			ppd_lensmodel: '',
			ppd_focallength: '',
			ppd_fnumber: '',
			ppd_exposuretime: '',
			ppd_iso: ''
		};
	return {
		media: {
			ppd_usermediaid: `mock-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
			ppd_zestyguid: '',
			ppd_imageurl: URL.createObjectURL(file),
			ppd_altdescription: suggestions ? suggestions.alt : '',
			ppd_categorytags: tags.join(','),
			ppd_displaytitle: '',
			ppd_displaydescription: '',
			...exif,
			ppd_isbackdrop: uploadAs === 'backdrop',
			ppd_isprofile: uploadAs === 'profile',
			alt: suggestions ? suggestions.alt : 'Member photo'
		},
		suggestions: suggestions ? {...suggestions, tags} : null
	};
}
